import { mergeStyles } from "react-select";
import type { GroupBase, StylesConfig } from "react-select";

// react-select ships inline light colors (white control and menu, #DEEBFF
// focus), which stay light in dark mode. These point the same slots at
// semantic tokens; the light values are within a shade of the defaults.
// Pass layout overrides (size, z-index) rather than colors.
export function themedSelectStyles<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  overrides?: StylesConfig<Option, IsMulti, Group>,
): StylesConfig<Option, IsMulti, Group> {
  const themed: StylesConfig<Option, IsMulti, Group> = {
    control: (base, state) => ({
      ...base,
      backgroundColor: state.isDisabled
        ? "var(--color-surface-disabled)"
        : "var(--color-surface-default)",
      borderColor: state.isDisabled
        ? "var(--color-borderDisabled)"
        : state.isFocused
          ? "var(--color-interactive-selected)"
          : "var(--color-borderDefault)",
      boxShadow: state.isFocused
        ? "0 0 0 1px var(--color-interactive-selected)"
        : "none",
      "&:hover": {
        borderColor: state.isFocused
          ? "var(--color-interactive-selected)"
          : "var(--color-iconMinimal)",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--color-surface-default)",
      border: "1px solid var(--color-borderSubtle)",
    }),
    option: (base, state) => ({
      ...base,
      color: state.isSelected
        ? "var(--color-action-primary-text)"
        : "var(--color-textDefault)",
      backgroundColor: state.isSelected
        ? "var(--color-action-primary)"
        : state.isFocused
          ? "var(--color-surface-selected)"
          : "transparent",
    }),
    singleValue: (base) => ({ ...base, color: "var(--color-textDefault)" }),
    input: (base) => ({ ...base, color: "var(--color-textDefault)" }),
    placeholder: (base) => ({ ...base, color: "var(--color-textSubtle)" }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "var(--color-borderDefault)",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--color-iconMinimal)",
    }),
    clearIndicator: (base) => ({ ...base, color: "var(--color-iconMinimal)" }),
    loadingIndicator: (base) => ({
      ...base,
      color: "var(--color-iconMinimal)",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "var(--color-surface-muted)",
    }),
    multiValueLabel: (base) => ({ ...base, color: "var(--color-textDefault)" }),
    groupHeading: (base) => ({ ...base, color: "var(--color-textSubtle)" }),
    noOptionsMessage: (base) => ({ ...base, color: "var(--color-textSubtle)" }),
    loadingMessage: (base) => ({ ...base, color: "var(--color-textSubtle)" }),
  };
  return overrides ? mergeStyles(themed, overrides) : themed;
}
